import './FilterViewFlyout.scss';
import { useDispatch, useSelector } from 'react-redux';
import { KWELLO_ICON } from '../../../components/icon/KwelloSvgIcon';

import { FlyoutBody } from '../../flyouts/Flyout';
import { selectFlyoutData, toggleFlyout } from 'common/store/slices/flyout.slice';
import { FilterButtonFilterObject } from 'common/components/widgets/widget-subcomponents/DashboardWidgetFilterButton';
import { FlyoutHeader } from 'common/components/flyouts/components/FlyoutHeader';
import {
  CommonFilterList,
  FilterConfig,
  FilterParent,
  FilterValueObject,
} from 'common/models/filter.model';
import { FILTER_MAP } from 'common/hooks/filters';
import { Tooltip } from 'react-tooltip';
import useUniversalFilters from 'modules/insights/hooks/useUniversalFilters';
import { formatFilterDateString } from 'common/hooks/utils';
import { selectOverrideDateFilter } from 'common/store/slices/dashboard-editor.slice';

interface FilterSortOrder {
  filterParent: FilterParent;
  filterKeys: string[];
}

const filtersSortOrder = ({ filterParent, filterKeys }: FilterSortOrder): string[] => {
  const config = FILTER_MAP[filterParent];

  const configKeys = Array.from(
    new Set(
      config.reduce((acc: string[], filter: FilterConfig) => {
        if (filter.property === (CommonFilterList.Survey as string)) {
          acc.push(CommonFilterList.Survey, CommonFilterList.Question, CommonFilterList.Response);
        } else {
          acc.push(filter.property);
        }

        return acc;
      }, [])
    )
  );

  const orderedKeys = configKeys.reduce((acc: string[], key: string) => {
    if (filterKeys.includes(key)) {
      acc.push(key);
    }
    return acc;
  }, []);

  return orderedKeys;
};

export default function FilterViewFlyout() {
  const dispatch = useDispatch();
  const { universalFilters: universalFiltersFlag, ...filters } = useSelector(
    selectFlyoutData
  ) as FilterButtonFilterObject;
  const filterData = filters.filters ?? {};
  const filterKeys = Object.keys(filterData);
  const { universalFilters } = useUniversalFilters();
  const universalFilterKeys = !universalFiltersFlag
    ? Object.keys(universalFilters?.filters ?? {})
    : [];
  const overrideDateFilter = useSelector(selectOverrideDateFilter);

  // Sort the filterkeys into the order they should be displayed
  const orderedKeys = filterKeys.length
    ? filtersSortOrder({
        filterParent: filters.filterParent,
        filterKeys,
      })
    : [];

  // Find Survey, Question, ResponseOption keys
  const sqrKeys = filterKeys.filter((key) =>
    ['Survey', 'Question', 'ResponseOption'].includes(key)
  );

  // Update the orderedKeys to include the Survey, Question, ResponseOption keys
  const updatedOrderedKeys = orderedKeys.map((key) => (key === 'Survey' ? sqrKeys : key)).flat();

  // Find Custom Attribute keys
  const customAttributeKeys = filterKeys.filter((key) => key.startsWith('attributes_'));

  const combinedKeys = Array.from(
    new Set([...updatedOrderedKeys, ...customAttributeKeys, ...universalFilterKeys])
  );

  const closeFlyout = () => {
    dispatch(toggleFlyout(false));
  };

  const dateString = formatFilterDateString({
    displayDateType: filters.visualDataType,
    startDate: filters.startDate,
    endDate: filters.endDate,
    visualData: filters.visualData,
  });

  const universalDateString =
    universalFilters &&
    formatFilterDateString({
      displayDateType: universalFilters.visualDataType,
      startDate: universalFilters.startDate,
      endDate: universalFilters.endDate,
      visualData: universalFilters.visualData,
    });

  const showDateFilter: boolean =
    (universalFiltersFlag && overrideDateFilter) || !universalFiltersFlag;
  return (
    <>
      <FlyoutHeader
        title="Applied Filters"
        subTitle="See all filters currently applied"
        onCloseClick={closeFlyout}
        icon={KWELLO_ICON.FILTER_ICON}
      />
      <FlyoutBody>
        <Tooltip
          id="universal-filters-tooltip"
          place="top"
          style={{ maxWidth: 200, fontSize: '0.8rem' }}
        >
          This filter is not active as it's currently overridden by the Universal Filters
        </Tooltip>

        {showDateFilter && (
          <div className="filter-section">
            <span className="filter-title">Date</span>
            <ul className="filter-values">
              {overrideDateFilter && !universalFiltersFlag && (
                <>
                  <li className="filter-value">{universalDateString}</li>
                  <li className="disabled-divider">|</li>
                  <li data-tooltip-id="universal-filters-tooltip" className="filter-value disabled">
                    {dateString}
                  </li>
                </>
              )}

              {(!overrideDateFilter || universalFiltersFlag) && (
                <li className="filter-value">{dateString}</li>
              )}
            </ul>
          </div>
        )}
        {combinedKeys.map((key) => (
          <div key={key} className="filter-section">
            <span className="filter-title">
              {filterData[key]?.label ?? universalFilters?.filters[key]?.label}
            </span>
            <ul className="filter-values">
              {!universalFiltersFlag &&
                universalFilters?.filters[key]?.value.map((value: FilterValueObject) => (
                  <li key={value.value} className={`filter-value`}>
                    {value.name}
                  </li>
                ))}
              {!universalFiltersFlag && filterData[key] && universalFilters?.filters[key] && (
                <li className="disabled-divider">|</li>
              )}
              {filterData[key]?.value.map((value: FilterValueObject) => (
                <li
                  data-tooltip-id={
                    !universalFiltersFlag && universalFilters?.filters[key]
                      ? 'universal-filters-tooltip'
                      : undefined
                  }
                  key={value.value}
                  className={`filter-value ${!universalFiltersFlag && universalFilters?.filters[key] ? 'disabled' : ''} `}
                >
                  {value.name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </FlyoutBody>
    </>
  );
}
